import type { RouteComponent } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { RouteRenderAnnouncer } from './RouteRenderAnnouncer';
import { RouteLoaderDataProvider } from './routeLoaderData';

type PreloadablePage<TProps extends object> = ComponentType<TProps> & Pick<RouteComponent, 'preload'>;

// TanStack clears a lazy route component's preload after the chunk loads, so
// read it when the router asks rather than retaining its initial value.
function exposeCurrentPagePreload<TComponent extends RouteComponent>(
  routeComponent: TComponent,
  Page: Pick<RouteComponent, 'preload'>
): TComponent {
  return Object.defineProperty(routeComponent, 'preload', { configurable: true, get: () => Page.preload });
}

export function createRouteComponent(Page: RouteComponent): RouteComponent {
  function RoutePageComponent() {
    return (
      <RouteLoaderDataProvider>
        <Page />
        <RouteRenderAnnouncer observeNavigation={true} />
      </RouteLoaderDataProvider>
    );
  }

  return exposeCurrentPagePreload(RoutePageComponent, Page);
}

export function createNotFoundRouteComponent(Page: RouteComponent): RouteComponent {
  function NotFoundRouteComponent() {
    return (
      <>
        <Page />
        <RouteRenderAnnouncer observeNavigation={false} />
      </>
    );
  }

  return exposeCurrentPagePreload(NotFoundRouteComponent, Page);
}

export function createRouteComponentWithProps<TProps extends object>(
  Page: PreloadablePage<TProps>,
  usePageProps: () => NoInfer<TProps>
): RouteComponent {
  function RoutePageComponentWithProps() {
    const pageProps = usePageProps();

    return (
      <RouteLoaderDataProvider>
        <Page {...pageProps} />
        <RouteRenderAnnouncer observeNavigation={true} />
      </RouteLoaderDataProvider>
    );
  }

  return exposeCurrentPagePreload(RoutePageComponentWithProps, Page);
}
